import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  collection, getDocs, updateDoc, doc, setDoc, deleteDoc,
  serverTimestamp, query, where, getDoc
} from 'firebase/firestore';

const generateCode = (company) => {
  const slug = company.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
  const year = new Date().getFullYear();
  return `${slug}-${year}`;
};

// Must match the companySlug helper in functions/index.js and Register.js
const companySlug = (name) => String(name || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export default function Engagements() {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const defaultAccessExpiry = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  };
  const [form, setForm] = useState({
    company: '',
    expiresAt: '',
    accessExpiresAt: defaultAccessExpiry(),
    notes: '',
    programName: 'From Curious to Capable: AI Literacy for the Modern Workforce',
    facilitatorName: 'Tamica Sears',
    seatLimit: '',
  });
  const [copied, setCopied] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [adminEmail, setAdminEmail] = useState({});
  const [adminCodeMap, setAdminCodeMap] = useState({});
  const [adminAssignedCodes, setAdminAssignedCodes] = useState({});
  const [adminMsg, setAdminMsg] = useState({});
  const [editingExpiry, setEditingExpiry] = useState({});
  const [newExpiryDate, setNewExpiryDate] = useState({});
  const [expiryMsg, setExpiryMsg] = useState({});
  const [editingAccessExpiry, setEditingAccessExpiry] = useState({});
  const [newAccessExpiryDate, setNewAccessExpiryDate] = useState({});
  const [accessExpiryMsg, setAccessExpiryMsg] = useState({});
  const [addingCode, setAddingCode] = useState({});
  const [newCode, setNewCode] = useState({});
  const [newCodeExpiry, setNewCodeExpiry] = useState({});
  const [codeMsg, setCodeMsg] = useState({});
  const [editingProgram, setEditingProgram] = useState({});
  const [programEdits, setProgramEdits] = useState({});
  const [programMsg, setProgramMsg] = useState({});
  const [companyPools, setCompanyPools] = useState({});
  const [editingSeats, setEditingSeats] = useState({});
  const [newSeatLimit, setNewSeatLimit] = useState({});
  const [seatMsg, setSeatMsg] = useState({});
  const [savingSeats, setSavingSeats] = useState({});

  useEffect(() => { loadEngagements(); }, []);

  const loadEngagements = async () => {
    const snap = await getDocs(collection(db, 'engagements'));
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setEngagements(all);
    // Load companyAdmins to show which codes each admin has
    const allAdminEmails = [...new Set(all.flatMap(e => e.admins || []))];
    if (allAdminEmails.length > 0) {
      const adminSnaps = await Promise.all(allAdminEmails.map(email => getDoc(doc(db, 'companyAdmins', email))));
      const map = {};
      adminSnaps.forEach((snap, i) => {
        if (snap.exists()) map[allAdminEmails[i]] = snap.data().companyCodes || [];
      });
      setAdminCodeMap(map);
    }
    // Load company seat pools
    try {
      const compSnap = await getDocs(collection(db, 'companies'));
      const pools = {};
      compSnap.docs.forEach(d => { pools[d.id] = d.data(); });
      setCompanyPools(pools);
    } catch { /* pools just will not display */ }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    const code = generateCode(form.company);
    await setDoc(doc(db, 'engagements', code), {
      code,
      company: form.company,
      expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
      accessExpiresAt: form.accessExpiresAt ? new Date(form.accessExpiresAt) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      notes: form.notes,
      programName: form.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
      facilitatorName: form.facilitatorName || 'Tamica Sears',
      active: true,
      postAssessmentUnlocked: false,
      videosEnabled: false,
      admins: [],
      createdAt: serverTimestamp(),
      participantCount: 0,
    });
    // Set up the shared seat pool if a cap was entered. If this company
    // already has a pool (second engagement for the same company), only the
    // cap updates. If participants already exist under this company name,
    // the counter starts from a live count so it is accurate on day one.
    const newCap = parseInt(form.seatLimit, 10);
    if (!isNaN(newCap) && newCap >= 0) {
      const slug = companySlug(form.company);
      if (slug) {
        try {
          const existing = companyPools[slug];
          let seatsUsed = existing && typeof existing.seatsUsed === 'number' ? existing.seatsUsed : null;
          if (seatsUsed === null) {
            const usersSnap = await getDocs(query(collection(db, 'users'), where('company', '==', form.company)));
            seatsUsed = usersSnap.size;
          }
          await setDoc(doc(db, 'companies', slug), {
            company: form.company,
            seatLimit: newCap,
            seatsUsed,
          }, { merge: true });
        } catch { /* pool can still be set from the card */ }
      }
    }
    setForm({
      company: '',
      expiresAt: '',
      accessExpiresAt: defaultAccessExpiry(),
      notes: '',
      programName: 'From Curious to Capable: AI Literacy for the Modern Workforce',
      facilitatorName: 'Tamica Sears',
      seatLimit: '',
    });
    setShowForm(false);
    await loadEngagements();
    setCreating(false);
  };

  const handleSaveProgramInfo = async (engId) => {
    const edits = programEdits[engId] || {};
    try {
      await updateDoc(doc(db, 'engagements', engId), {
        programName: edits.programName,
        facilitatorName: edits.facilitatorName,
        facilitatorTitle: edits.facilitatorTitle,
      });
      setEngagements(prev => prev.map(e => e.id === engId ? { ...e, ...edits } : e));
      setEditingProgram(prev => ({ ...prev, [engId]: false }));
      setProgramMsg({ [engId]: { type: 'success', text: 'Program info updated.' } });
      setTimeout(() => setProgramMsg({}), 3000);
    } catch {
      setProgramMsg({ [engId]: { type: 'error', text: 'Could not save. Please try again.' } });
    }
  };

  const toggleActive = async (id, current) => {
    await updateDoc(doc(db, 'engagements', id), { active: !current });
    setEngagements(prev => prev.map(e => e.id === id ? { ...e, active: !current } : e));
  };

  const handleSaveSeatLimit = async (eng) => {
    const slug = companySlug(eng.company);
    const limit = parseInt(newSeatLimit[eng.id], 10);
    if (!slug || isNaN(limit) || limit < 0) {
      setSeatMsg({ [eng.id]: { type: 'error', text: 'Enter a valid seat count.' } });
      return;
    }
    setSavingSeats(prev => ({ ...prev, [eng.id]: true }));
    try {
      const existing = companyPools[slug];
      let seatsUsed = existing && typeof existing.seatsUsed === 'number' ? existing.seatsUsed : null;
      if (seatsUsed === null) {
        // First cap ever set for this company. Initialize the counter with a
        // live count of existing participants so the pool is accurate on day one.
        const usersSnap = await getDocs(query(collection(db, 'users'), where('company', '==', eng.company)));
        seatsUsed = usersSnap.size;
      }
      await setDoc(doc(db, 'companies', slug), {
        company: eng.company,
        seatLimit: limit,
        seatsUsed,
      }, { merge: true });
      setCompanyPools(prev => ({ ...prev, [slug]: { ...(prev[slug] || {}), company: eng.company, seatLimit: limit, seatsUsed } }));
      setEditingSeats(prev => ({ ...prev, [eng.id]: false }));
      setSeatMsg({ [eng.id]: { type: 'success', text: `Seat cap set to ${limit} for ${eng.company}. ${seatsUsed} seat${seatsUsed !== 1 ? 's' : ''} currently in use.` } });
      setTimeout(() => setSeatMsg({}), 4000);
    } catch {
      setSeatMsg({ [eng.id]: { type: 'error', text: 'Could not save the seat cap. Try again.' } });
    }
    setSavingSeats(prev => ({ ...prev, [eng.id]: false }));
  };

  const toggleVideosEnabled = async (id, current) => {
    await updateDoc(doc(db, 'engagements', id), { videosEnabled: !current });
    setEngagements(prev => prev.map(e => e.id === id ? { ...e, videosEnabled: !current } : e));
  };

  const togglePostUnlocked = async (id, current) => {
    await updateDoc(doc(db, 'engagements', id), { postAssessmentUnlocked: !current });
    setEngagements(prev => prev.map(e => e.id === id ? { ...e, postAssessmentUnlocked: !current } : e));
  };

  const handleUpdateExpiry = async (engId) => {
    const date = newExpiryDate[engId];
    if (!date) return;
    try {
      const newDate = new Date(date);
      const isNowActive = newDate > new Date();
      await updateDoc(doc(db, 'engagements', engId), {
        expiresAt: newDate,
        ...(isNowActive ? { active: true } : {}),
      });
      setEngagements(prev => prev.map(e =>
        e.id === engId ? { ...e, expiresAt: newDate, ...(isNowActive ? { active: true } : {}) } : e
      ));
      setEditingExpiry(prev => ({ ...prev, [engId]: false }));
      setExpiryMsg({ [engId]: { type: 'success', text: isNowActive ? 'Date updated. Engagement is now active.' : 'Date updated.' } });
      setTimeout(() => setExpiryMsg({}), 3000);
    } catch {
      setExpiryMsg({ [engId]: { type: 'error', text: 'Could not update. Please try again.' } });
    }
  };

  const handleClearExpiry = async (engId) => {
    try {
      await updateDoc(doc(db, 'engagements', engId), { expiresAt: null, active: true });
      setEngagements(prev => prev.map(e => e.id === engId ? { ...e, expiresAt: null, active: true } : e));
      setEditingExpiry(prev => ({ ...prev, [engId]: false }));
      setExpiryMsg({ [engId]: { type: 'success', text: 'Set to no expiry. Registration is open.' } });
      setTimeout(() => setExpiryMsg({}), 3000);
    } catch {
      setExpiryMsg({ [engId]: { type: 'error', text: 'Could not update. Please try again.' } });
    }
  };

  const handleUpdateAccessExpiry = async (engId) => {
    const date = newAccessExpiryDate[engId];
    if (!date) return;
    try {
      const newDate = new Date(date);
      await updateDoc(doc(db, 'engagements', engId), { accessExpiresAt: newDate });
      setEngagements(prev => prev.map(e => e.id === engId ? { ...e, accessExpiresAt: newDate } : e));
      setEditingAccessExpiry(prev => ({ ...prev, [engId]: false }));
      setAccessExpiryMsg({ [engId]: { type: 'success', text: 'Access expiry updated.' } });
      setTimeout(() => setAccessExpiryMsg({}), 3000);
    } catch {
      setAccessExpiryMsg({ [engId]: { type: 'error', text: 'Could not update. Please try again.' } });
    }
  };

  const handleAddCode = async (parentEng) => {
    const code = (newCode[parentEng.id] || '').toUpperCase().trim();
    const expiry = newCodeExpiry[parentEng.id];
    if (!code) { setCodeMsg({ [parentEng.id]: { type: 'error', text: 'Enter a code first.' } }); return; }
    try {
      const existing = await getDoc(doc(db, 'engagements', code));
      if (existing.exists()) {
        setCodeMsg({ [parentEng.id]: { type: 'error', text: 'That code already exists.' } });
        return;
      }
      await setDoc(doc(db, 'engagements', code), {
        code,
        company: parentEng.company,
        parentCode: parentEng.id,
        expiresAt: expiry ? new Date(expiry) : null,
        accessExpiresAt: newAccessExpiryDate[parentEng.id] ? new Date(newAccessExpiryDate[parentEng.id]) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
        active: true,
        postAssessmentUnlocked: false,
        videosEnabled: false,
        admins: [],
        createdAt: serverTimestamp(),
        participantCount: 0,
        notes: `Additional code for ${parentEng.company}`,
        programName: parentEng.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
        facilitatorName: parentEng.facilitatorName || 'Tamica Sears',
      });
      setNewCode(prev => ({ ...prev, [parentEng.id]: '' }));
      setNewCodeExpiry(prev => ({ ...prev, [parentEng.id]: '' }));
      setAddingCode(prev => ({ ...prev, [parentEng.id]: false }));
      setCodeMsg({ [parentEng.id]: { type: 'success', text: `Code ${code} created for ${parentEng.company}.` } });
      setTimeout(() => setCodeMsg({}), 3000);
      await loadEngagements();
    } catch {
      setCodeMsg({ [parentEng.id]: { type: 'error', text: 'Could not add code. Please try again.' } });
    }
  };

  // Registration stores users.email exactly as the person typed it, so an
  // exact match on the lowercased input can miss. Fall back to scanning the
  // engagement's own users and comparing case-insensitively.
  const findUserByEmail = async (email, codes) => {
    const exact = await getDocs(query(collection(db, 'users'), where('email', '==', email)));
    if (!exact.empty) return exact.docs[0];
    for (const code of codes) {
      const snap = await getDocs(query(collection(db, 'users'), where('companyCode', '==', code)));
      const hit = snap.docs.find(d => (d.data().email || '').trim().toLowerCase() === email);
      if (hit) return hit;
    }
    return null;
  };

  const addAdmin = async (engId, company) => {
    const email = (adminEmail[engId] || '').trim().toLowerCase();
    if (!email) return;
    setAdminMsg({});
    try {
      const selectedCodes = adminAssignedCodes[engId]?.length ? adminAssignedCodes[engId] : [engId];
      const existingSnap = await getDoc(doc(db, 'companyAdmins', email));
      const existingCodes = existingSnap.exists() ? (existingSnap.data().companyCodes || []) : [];
      const mergedCodes = [...new Set([...existingCodes, ...selectedCodes])];
      await setDoc(doc(db, 'companyAdmins', email), {
        email, companyCodes: mergedCodes, company, companyName: company,
        addedAt: new Date().toISOString(),
      }, { merge: true });
      const userDoc = await findUserByEmail(email, selectedCodes);
      if (userDoc) {
        await updateDoc(doc(db, 'users', userDoc.id), {
          adminRole: 'companyadmin', adminCompanyCode: selectedCodes[0], adminCompany: company,
        });
      }
      const eng = engagements.find(e => e.id === engId);
      const current = eng?.admins || [];
      if (!current.includes(email)) {
        await updateDoc(doc(db, 'engagements', engId), { admins: [...current, email] });
        setEngagements(prev => prev.map(e => e.id === engId ? { ...e, admins: [...(e.admins || []), email] } : e));
      }
      const note = userDoc ? '' : " They haven't registered yet; admin access starts the first time they sign in.";
      setAdminMsg({ [engId]: { type: 'success', text: `${email} added as company admin for: ${mergedCodes.join(', ')}.${note}` } });
      setAdminEmail(prev => ({ ...prev, [engId]: '' }));
      setAdminAssignedCodes(prev => ({ ...prev, [engId]: [] }));
    } catch {
      setAdminMsg({ [engId]: { type: 'error', text: 'Something went wrong. Try again.' } });
    }
  };

  const removeAdmin = async (engId, email) => {
    // Revoke the server-side permission for this code first; the login routing
    // and the security rules both honor companyAdmins, so leaving it behind
    // would keep them an admin.
    const caRef = doc(db, 'companyAdmins', email);
    const caSnap = await getDoc(caRef);
    let remaining = [];
    if (caSnap.exists()) {
      remaining = (caSnap.data().companyCodes || []).filter(c => c !== engId);
      if (remaining.length) {
        await setDoc(caRef, { companyCodes: remaining }, { merge: true });
      } else {
        await deleteDoc(caRef);
      }
    }
    const userDoc = await findUserByEmail((email || '').trim().toLowerCase(), [engId]);
    if (userDoc) {
      if (remaining.length) {
        await updateDoc(doc(db, 'users', userDoc.id), { adminCompanyCode: remaining[0] });
      } else {
        await updateDoc(doc(db, 'users', userDoc.id), { adminRole: 'participant', adminCompanyCode: null, adminCompany: null });
      }
    }
    setAdminCodeMap(prev => {
      const next = { ...prev };
      const remaining = (next[email] || []).filter(c => c !== engId);
      if (remaining.length) next[email] = remaining; else delete next[email];
      return next;
    });
    const eng = engagements.find(e => e.id === engId);
    const updated = (eng?.admins || []).filter(a => a !== email);
    await updateDoc(doc(db, 'engagements', engId), { admins: updated });
    setEngagements(prev => prev.map(e => e.id === engId ? { ...e, admins: updated } : e));
  };

  const copyCode = (code) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(''), 2000); };
  const formatDate = (val) => { if (!val) return 'No expiry'; const d = val.toDate ? val.toDate() : new Date(val); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
  const isExpired = (val) => { if (!val) return false; return (val.toDate ? val.toDate() : new Date(val)) < new Date(); };
  const toInputDate = (val) => { if (!val) return ''; try { return (val.toDate ? val.toDate() : new Date(val)).toISOString().slice(0, 10); } catch { return ''; } };
  const getCompanyCodes = (company) => engagements.filter(e => e.company === company).map(e => e.id);
  const toggleAssignedCode = (engId, code) => {
    setAdminAssignedCodes(prev => {
      const current = prev[engId] || [engId];
      return { ...prev, [engId]: current.includes(code) ? current.filter(c => c !== code) : [...current, code] };
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <div style={s.heading}>Client Engagements</div>
          <div style={s.sub}>Create a code for each client. They share it with their team for self-registration.</div>
        </div>
        <button style={s.createBtn} onClick={() => setShowForm(v => !v)}>{showForm ? 'Cancel' : '+ New Engagement'}</button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={s.form}>
          <div style={s.formTitle}>New Client Engagement</div>
          <div style={s.formGrid}>
            <div style={s.field}>
              <label style={s.label}>Company Name</label>
              <input style={s.input} type="text" placeholder="e.g. Acme Corporation" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} required />
            </div>
            <div style={s.field}>
              <label style={s.label}>Registration Closes</label>
              <input style={s.input} type="date" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Seat Cap (optional)</label>
              <input style={s.input} type="number" min="0" placeholder="e.g. 30" value={form.seatLimit} onChange={e => setForm(f => ({ ...f, seatLimit: e.target.value }))} />
              <div style={{ fontSize: '11px', color: '#8A8A8A', marginTop: '4px' }}>Shared across every code for this company. Leave blank for no cap, you can set one later from the card.</div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Participant Access Expires</label>
              <input style={s.input} type="date" value={form.accessExpiresAt} onChange={e => setForm(f => ({ ...f, accessExpiresAt: e.target.value }))} />
              <div style={{ fontSize: '11px', color: '#8A8A8A', marginTop: '4px' }}>Defaults to 12 months from today. Participants lose portal access after this date.</div>
            </div>
          </div>
          <div style={s.field}>
            <label style={s.label}>Program Name</label>
            <input
              style={s.input}
              type="text"
              placeholder="e.g. From Curious to Capable: AI Literacy for the Modern Workforce"
              value={form.programName}
              onChange={e => setForm(f => ({ ...f, programName: e.target.value }))}
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Facilitator Name</label>
            <input
              style={s.input}
              type="text"
              placeholder="e.g. Tamica Sears"
              value={form.facilitatorName}
              onChange={e => setForm(f => ({ ...f, facilitatorName: e.target.value }))}
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Notes (optional)</label>
            <input style={s.input} type="text" placeholder="e.g. Q1 cohort, HR department" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          {form.company && <div style={s.codePreview}>Generated code: <strong>{generateCode(form.company)}</strong></div>}
          <button style={s.submitBtn} type="submit" disabled={creating}>{creating ? 'Creating...' : 'Create Engagement →'}</button>
        </form>
      )}

      {(() => {
        const warnings = engagements.filter(eng => {
          if (!eng.accessExpiresAt) return false;
          const d = eng.accessExpiresAt.toDate ? eng.accessExpiresAt.toDate() : new Date(eng.accessExpiresAt);
          const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
          return days > 0 && days <= 15;
        });
        if (warnings.length === 0) return null;
        return (
          <div style={{ background: '#fef5e4', border: '1.5px solid #f5d78a', padding: '14px 20px', marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#854f0b', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Access Expiring Soon</div>
            {warnings.map(eng => {
              const d = eng.accessExpiresAt.toDate ? eng.accessExpiresAt.toDate() : new Date(eng.accessExpiresAt);
              const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={eng.id} style={{ fontSize: '13px', color: '#3D3D3D' }}>
                  <strong>{eng.company}</strong> ({eng.id}): participant access expires in <strong style={{ color: '#854f0b' }}>{days} day{days !== 1 ? 's' : ''}</strong> on {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}. Extend the access date or participants will be locked out.
                </div>
              );
            })}
          </div>
        );
      })()}

      {loading ? <div style={{ color: '#3D3D3D', fontSize: '14px' }}>Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {engagements.length === 0 && <div style={s.empty}>No engagements yet. Create your first one above.</div>}
          {engagements.map(eng => {
            const expired = isExpired(eng.expiresAt);
            const statusLabel = !eng.active ? 'Deactivated' : expired ? 'Expired' : 'Active';
            const statusStyle = !eng.active || expired ? s.statusInactive : s.statusActive;
            const expanded = expandedId === eng.id;
            const companyCodes = getCompanyCodes(eng.company);
            const selectedCodes = adminAssignedCodes[eng.id]?.length ? adminAssignedCodes[eng.id] : [eng.id];
            const isEditingProgram = editingProgram[eng.id];
            const currentProgramEdits = programEdits[eng.id] || {
              programName: eng.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
              facilitatorName: eng.facilitatorName || 'Tamica Sears',
              facilitatorTitle: eng.facilitatorTitle || 'Founder, The Corporate Fixers',
            };

            return (
              <div key={eng.id} style={{ ...s.engCard, opacity: eng.active ? 1 : 0.65 }}>
                <div style={s.engTop}>
                  <div>
                    <div style={s.engCompany}>{eng.company}</div>
                    {eng.notes && <div style={s.engNotes}>{eng.notes}</div>}
                    <div style={s.engMeta2}>{(eng.admins || []).length} company admin{(eng.admins || []).length !== 1 ? 's' : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={{ ...s.statusBadge, ...statusStyle }}>{statusLabel}</span>
                    <button style={s.toggleBtn} onClick={() => toggleActive(eng.id, eng.active)}>{eng.active ? 'Deactivate' : 'Reactivate'}</button>
                    <button style={s.expandBtn} onClick={() => setExpandedId(expanded ? null : eng.id)}>{expanded ? 'Collapse ▲' : 'Manage ▼'}</button>
                  </div>
                </div>

                <div style={s.engBottom}>
                  <div style={s.codeBox}>
                    <span style={s.codeText}>{eng.code || eng.id}</span>
                    <button style={s.copyBtn} onClick={() => copyCode(eng.code || eng.id)}>{copied === (eng.code || eng.id) ? 'Copied!' : 'Copy Code'}</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    <div style={s.engMeta}>Registration closes: <strong>{formatDate(eng.expiresAt)}</strong></div>
                    {editingExpiry[eng.id] ? (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="date" style={{ ...s.input, padding: '5px 10px', width: 'auto', fontSize: '12px' }}
                          value={newExpiryDate[eng.id] || toInputDate(eng.expiresAt)}
                          onChange={e => setNewExpiryDate(prev => ({ ...prev, [eng.id]: e.target.value }))} />
                        <button style={s.submitBtn} onClick={() => handleUpdateExpiry(eng.id)}>Save</button>
                        <button style={s.toggleBtn} onClick={() => handleClearExpiry(eng.id)}>No expiry</button>
                        <button style={s.toggleBtn} onClick={() => setEditingExpiry(prev => ({ ...prev, [eng.id]: false }))}>Cancel</button>
                      </div>
                    ) : (
                      <button style={{ ...s.toggleBtn, color: expired ? '#C0392B' : '#1B2A4A', borderColor: expired ? '#f5c6c6' : '#E8E6E2' }}
                        onClick={() => setEditingExpiry(prev => ({ ...prev, [eng.id]: true }))}>
                        {expired ? '⟳ Extend / Reactivate' : 'Change Date'}
                      </button>
                    )}
                    {expiryMsg[eng.id] && <div style={{ ...s.msgBox, ...(expiryMsg[eng.id].type === 'error' ? s.msgError : s.msgSuccess) }}>{expiryMsg[eng.id].text}</div>}
                    <div style={{ borderTop: '1px solid #E8E6E2', paddingTop: '6px', marginTop: '2px', width: '100%', textAlign: 'right' }}>
                      {(() => {
                        const accessDate = eng.accessExpiresAt ? (eng.accessExpiresAt.toDate ? eng.accessExpiresAt.toDate() : new Date(eng.accessExpiresAt)) : null;
                        const daysLeft = accessDate ? Math.ceil((accessDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                        const accessWarning = daysLeft !== null && daysLeft <= 15 && daysLeft > 0;
                        const accessExpired = daysLeft !== null && daysLeft <= 0;
                        return (
                          <>
                            <div style={{ ...s.engMeta, color: accessWarning ? '#854f0b' : accessExpired ? '#C0392B' : '#3D3D3D' }}>
                              Participant access expires: <strong>{accessDate ? accessDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}</strong>
                              {accessWarning && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', background: '#fef5e4', color: '#854f0b', padding: '2px 6px' }}>{daysLeft} days left</span>}
                              {accessExpired && <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: '700', background: '#fdecea', color: '#C0392B', padding: '2px 6px' }}>Expired</span>}
                            </div>
                            {editingAccessExpiry[eng.id] ? (
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px', justifyContent: 'flex-end' }}>
                                <input type="date" style={{ ...s.input, padding: '5px 10px', width: 'auto', fontSize: '12px' }}
                                  value={newAccessExpiryDate[eng.id] || toInputDate(eng.accessExpiresAt)}
                                  onChange={e => setNewAccessExpiryDate(prev => ({ ...prev, [eng.id]: e.target.value }))} />
                                <button style={s.submitBtn} onClick={() => handleUpdateAccessExpiry(eng.id)}>Save</button>
                                <button style={s.toggleBtn} onClick={() => setEditingAccessExpiry(prev => ({ ...prev, [eng.id]: false }))}>Cancel</button>
                              </div>
                            ) : (
                              <button style={{ ...s.toggleBtn, marginTop: '4px' }} onClick={() => setEditingAccessExpiry(prev => ({ ...prev, [eng.id]: true }))}>
                                {accessExpired ? '⟳ Extend Access' : 'Change Access Date'}
                              </button>
                            )}
                            {accessExpiryMsg[eng.id] && <div style={{ ...s.msgBox, ...(accessExpiryMsg[eng.id].type === 'error' ? s.msgError : s.msgSuccess), marginTop: '4px' }}>{accessExpiryMsg[eng.id].text}</div>}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div style={s.postUnlockRow}>
                  <div>
                    <div style={s.postUnlockLabel}>Post-Training Assessment</div>
                    <div style={s.postUnlockSub}>Unlock when the program concludes. Participants will see the post assessment automatically.</div>
                  </div>
                  <button style={{ ...s.unlockBtn, ...(eng.postAssessmentUnlocked ? s.unlockBtnActive : {}) }} onClick={() => togglePostUnlocked(eng.id, eng.postAssessmentUnlocked)}>
                    {eng.postAssessmentUnlocked ? '✓ Unlocked for All' : 'Unlock Post Assessment'}
                  </button>
                </div>

                <div style={s.postUnlockRow}>
                  <div>
                    <div style={s.postUnlockLabel}>Async Video Lessons</div>
                    <div style={s.postUnlockSub}>Turn this on to give every participant in this cohort access to the day-by-day video library.</div>
                  </div>
                  <button style={{ ...s.unlockBtn, ...(eng.videosEnabled ? s.unlockBtnActive : {}) }} onClick={() => toggleVideosEnabled(eng.id, eng.videosEnabled)}>
                    {eng.videosEnabled ? '✓ Videos Enabled' : 'Enable Videos'}
                  </button>
                </div>

                {/* SHARED SEAT POOL */}
                {!eng.parentCode ? (
                  <div style={{ ...s.postUnlockRow, borderTop: '1px solid #E8E6E2', marginTop: '16px', paddingTop: '16px' }}>
                    <div>
                      <div style={s.postUnlockLabel}>Shared Seat Pool</div>
                      <div style={s.postUnlockSub}>One pool for all {eng.company} codes. Registration blocks when the pool fills, and deleting a participant frees the seat automatically.</div>
                      {seatMsg[eng.id] && <div style={{ ...s.msgBox, ...(seatMsg[eng.id].type === 'error' ? s.msgError : s.msgSuccess), marginTop: '6px' }}>{seatMsg[eng.id].text}</div>}
                    </div>
                    {(() => {
                      const pool = companyPools[companySlug(eng.company)];
                      const used = pool && typeof pool.seatsUsed === 'number' ? pool.seatsUsed : 0;
                      const limit = pool && typeof pool.seatLimit === 'number' ? pool.seatLimit : null;
                      const full = limit !== null && used >= limit;
                      if (editingSeats[eng.id]) {
                        return (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                            <input type="number" min="0" placeholder="Seats"
                              style={{ ...s.input, width: '90px', padding: '6px 10px' }}
                              value={newSeatLimit[eng.id] !== undefined ? newSeatLimit[eng.id] : (limit !== null ? limit : '')}
                              onChange={e => setNewSeatLimit(prev => ({ ...prev, [eng.id]: e.target.value }))} />
                            <button style={s.submitBtn} disabled={savingSeats[eng.id]} onClick={() => handleSaveSeatLimit(eng)}>{savingSeats[eng.id] ? 'Saving...' : 'Save'}</button>
                            <button style={s.toggleBtn} onClick={() => setEditingSeats(prev => ({ ...prev, [eng.id]: false }))}>Cancel</button>
                          </div>
                        );
                      }
                      return (
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
                          <span style={{ ...s.statusBadge, ...(full ? s.statusInactive : s.statusActive) }}>
                            {limit === null ? `${used} seat${used !== 1 ? 's' : ''} used · no cap` : `${used} of ${limit} seats used`}
                          </span>
                          <button style={s.toggleBtn} onClick={() => {
                            setNewSeatLimit(prev => ({ ...prev, [eng.id]: limit !== null ? String(limit) : '' }));
                            setEditingSeats(prev => ({ ...prev, [eng.id]: true }));
                          }}>
                            {limit === null ? 'Set Seat Cap' : 'Edit Cap'}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ borderTop: '1px solid #E8E6E2', marginTop: '16px', paddingTop: '12px', fontSize: '11px', color: '#8A8A8A' }}>
                    Draws from the {eng.company} shared seat pool. Manage the cap on the main {eng.company} card.
                  </div>
                )}

                {/* PROGRAM INFO SECTION */}
                <div style={{ borderTop: '1px solid #E8E6E2', marginTop: '16px', paddingTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={s.postUnlockLabel}>Certificate Info</div>
                      {!isEditingProgram && (
                        <div style={{ marginTop: '6px' }}>
                          <div style={{ fontSize: '12px', color: '#3D3D3D', marginBottom: '3px' }}>
                            <span style={{ color: '#8A8A8A', fontSize: '11px' }}>Program: </span>
                            {eng.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#3D3D3D' }}>
                            <span style={{ color: '#8A8A8A', fontSize: '11px' }}>Facilitator: </span>
                            {eng.facilitatorName || 'Tamica Sears'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#3D3D3D' }}>
                            <span style={{ color: '#8A8A8A', fontSize: '11px' }}>Title: </span>
                            {eng.facilitatorTitle || 'Founder, The Corporate Fixers'}
                          </div>
                        </div>
                      )}
                    </div>
                    {!isEditingProgram && (
                      <button style={s.toggleBtn} onClick={() => {
                        setEditingProgram(prev => ({ ...prev, [eng.id]: true }));
                        setProgramEdits(prev => ({
                          ...prev,
                          [eng.id]: {
                            programName: eng.programName || 'From Curious to Capable: AI Literacy for the Modern Workforce',
                            facilitatorName: eng.facilitatorName || 'Tamica Sears',
                          }
                        }));
                      }}>Edit</button>
                    )}
                  </div>

                  {isEditingProgram && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={s.field}>
                        <label style={s.label}>Program Name</label>
                        <input
                          style={s.input}
                          type="text"
                          value={currentProgramEdits.programName}
                          onChange={e => setProgramEdits(prev => ({ ...prev, [eng.id]: { ...currentProgramEdits, programName: e.target.value } }))}
                        />
                      </div>
                      <div style={s.field}>
                        <label style={s.label}>Facilitator Name</label>
                        <input
                          style={s.input}
                          type="text"
                          value={currentProgramEdits.facilitatorName}
                          onChange={e => setProgramEdits(prev => ({ ...prev, [eng.id]: { ...currentProgramEdits, facilitatorName: e.target.value } }))}
                        />
                      </div>
                      <div style={s.field}>
                        <label style={s.label}>Facilitator Title</label>
                        <input
                          style={s.input}
                          type="text"
                          value={currentProgramEdits.facilitatorTitle || ''}
                          onChange={e => setProgramEdits(prev => ({ ...prev, [eng.id]: { ...currentProgramEdits, facilitatorTitle: e.target.value } }))}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={s.submitBtn} onClick={() => handleSaveProgramInfo(eng.id)}>Save</button>
                        <button style={s.toggleBtn} onClick={() => setEditingProgram(prev => ({ ...prev, [eng.id]: false }))}>Cancel</button>
                      </div>
                      {programMsg[eng.id] && <div style={{ ...s.msgBox, ...(programMsg[eng.id].type === 'error' ? s.msgError : s.msgSuccess) }}>{programMsg[eng.id].text}</div>}
                    </div>
                  )}
                </div>

                {expanded && (
                  <div style={s.expandPanel}>
                    <div style={{ marginBottom: '24px' }}>
                      <div style={s.panelTitle}>Additional Codes</div>
                      <div style={s.panelSub}>Add another registration code for the same company, for separate departments or cohorts. Both codes tag participants to {eng.company}.</div>

                      {addingCode[eng.id] ? (
                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div>
                              <label style={s.label}>New Code</label>
                              <input style={{ ...s.input, textTransform: 'uppercase', letterSpacing: '2px', fontWeight: '700', width: '180px' }}
                                type="text" placeholder={`e.g. ${(eng.code || eng.id)}-2`}
                                value={newCode[eng.id] || ''}
                                onChange={e => setNewCode(prev => ({ ...prev, [eng.id]: e.target.value.toUpperCase() }))} />
                            </div>
                            <div>
                              <label style={s.label}>Registration Closes</label>
                              <input style={{ ...s.input, width: '160px' }} type="date"
                                value={newCodeExpiry[eng.id] || ''}
                                onChange={e => setNewCodeExpiry(prev => ({ ...prev, [eng.id]: e.target.value }))} />
                            </div>
                            <div>
                              <label style={s.label}>Access Expires</label>
                              <input style={{ ...s.input, width: '160px' }} type="date"
                                value={newAccessExpiryDate[eng.id] || defaultAccessExpiry()}
                                onChange={e => setNewAccessExpiryDate(prev => ({ ...prev, [eng.id]: e.target.value }))} />
                            </div>
                          </div>
                          <button style={s.submitBtn} onClick={() => handleAddCode(eng)}>Add Code →</button>
                          <button style={s.toggleBtn} onClick={() => setAddingCode(prev => ({ ...prev, [eng.id]: false }))}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                          <button style={s.toggleBtn} onClick={() => setAddingCode(prev => ({ ...prev, [eng.id]: true }))}>+ Add Another Code</button>
                          {companyCodes.filter(c => c !== eng.id).length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                              <span style={{ fontSize: '11px', color: '#8A8A8A' }}>Other codes:</span>
                              {companyCodes.filter(c => c !== eng.id).map(c => (
                                <span key={c} style={s.siblingCode}>{c}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {codeMsg[eng.id] && <div style={{ ...s.msgBox, ...(codeMsg[eng.id].type === 'error' ? s.msgError : s.msgSuccess) }}>{codeMsg[eng.id].text}</div>}
                    </div>

                    <div style={{ borderTop: '1px solid #E8E6E2', paddingTop: '20px' }}>
                      <div style={s.panelTitle}>Company Admins</div>
                      <div style={s.panelSub}>Admins listed here have access to this code. Assign access to multiple codes when adding an admin.</div>

                      {(() => {
                        const adminsForThisCode = Object.entries(adminCodeMap).filter(([email, codes]) => codes.includes(eng.id));
                        return adminsForThisCode.length > 0 ? (
                          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {adminsForThisCode.map(([email]) => (
                              <div key={email} style={s.adminRow}>
                                <span style={s.adminEmail}>{email}</span>
                                <button style={s.removeAdminBtn} onClick={() => removeAdmin(eng.id, email)}>Remove</button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: '#8A8A8A', marginTop: '8px' }}>No admins assigned to this code yet.</div>
                        );
                      })()}

                      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <input style={s.input} type="email" placeholder="admin@clientcompany.com"
                          value={adminEmail[eng.id] || ''}
                          onChange={e => setAdminEmail(prev => ({ ...prev, [eng.id]: e.target.value }))} />

                        {companyCodes.length > 1 && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: '#8A8A8A', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' }}>Assign access to:</div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {companyCodes.map(code => (
                                <label key={code} style={{
                                  display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer',
                                  background: selectedCodes.includes(code) ? '#1B2A4A' : '#F2F0EC',
                                  color: selectedCodes.includes(code) ? '#D4A847' : '#3D3D3D',
                                  padding: '5px 12px', fontWeight: selectedCodes.includes(code) ? '600' : '400',
                                }}>
                                  <input type="checkbox" checked={selectedCodes.includes(code)}
                                    onChange={() => toggleAssignedCode(eng.id, code)} style={{ display: 'none' }} />
                                  {code}
                                </label>
                              ))}
                            </div>
                          </div>
                        )}

                        <button style={{ ...s.addAdminBtn, alignSelf: 'flex-start' }} onClick={() => addAdmin(eng.id, eng.company)}>Add Admin →</button>
                      </div>

                      {adminMsg[eng.id] && (
                        <div style={{ ...s.msgBox, ...(adminMsg[eng.id].type === 'error' ? s.msgError : s.msgSuccess), marginTop: '8px' }}>
                          {adminMsg[eng.id].text}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  heading: { fontFamily: 'Playfair Display, serif', fontSize: '26px', fontWeight: '900', color: '#1B2A4A', marginBottom: '4px' },
  sub: { fontSize: '13px', color: '#3D3D3D', fontWeight: '300' },
  createBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 20px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  form: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '24px', marginBottom: '24px' },
  formTitle: { fontFamily: 'Playfair Display, serif', fontSize: '17px', color: '#1B2A4A', marginBottom: '16px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  field: { marginBottom: '4px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#1B2A4A', marginBottom: '5px', fontFamily: 'DM Sans, sans-serif' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '10px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#3D3D3D', background: '#fff', outline: 'none', boxSizing: 'border-box' },
  codePreview: { fontSize: '13px', color: '#3D3D3D', marginBottom: '16px', background: '#F2F0EC', padding: '10px 14px' },
  submitBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 20px', fontFamily: 'Playfair Display, serif', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  empty: { textAlign: 'center', padding: '40px', color: '#3D3D3D', fontSize: '13px', background: '#FFFFFF', border: '1.5px solid #E8E6E2' },
  engCard: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', padding: '20px 24px' },
  engTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  engCompany: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', fontWeight: '700' },
  engNotes: { fontSize: '12px', color: '#3D3D3D', marginTop: '2px', fontWeight: '300' },
  engMeta2: { fontSize: '11px', color: '#3D3D3D', marginTop: '4px' },
  statusBadge: { fontSize: '10px', fontWeight: '600', padding: '3px 10px' },
  statusActive: { background: '#eaf3de', color: '#3b6d11' },
  statusInactive: { background: '#fdecea', color: '#a32d2d' },
  toggleBtn: { fontSize: '11px', color: '#3D3D3D', background: 'none', border: '1px solid #E8E6E2', padding: '4px 10px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  expandBtn: { fontSize: '11px', color: '#1B2A4A', background: '#F2F0EC', border: '1px solid #E8E6E2', padding: '4px 10px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: '600' },
  engBottom: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #E8E6E2', gap: '16px', flexWrap: 'wrap' },
  codeBox: { display: 'flex', alignItems: 'center', gap: '10px' },
  codeText: { fontFamily: 'monospace', fontSize: '18px', fontWeight: '700', color: '#1B2A4A', letterSpacing: '2px', background: '#F2F0EC', padding: '6px 14px' },
  copyBtn: { background: '#D4A847', color: '#1B2A4A', border: 'none', padding: '6px 14px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  engMeta: { fontSize: '12px', color: '#3D3D3D' },
  postUnlockRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' },
  postUnlockLabel: { fontSize: '13px', fontWeight: '600', color: '#1B2A4A', marginBottom: '2px' },
  postUnlockSub: { fontSize: '11px', color: '#3D3D3D', fontWeight: '300', maxWidth: '400px' },
  unlockBtn: { background: '#F2F0EC', color: '#3D3D3D', border: '1.5px solid #E8E6E2', padding: '8px 16px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap', flexShrink: 0 },
  unlockBtnActive: { background: '#eaf3de', color: '#3b6d11', borderColor: '#3b6d11' },
  expandPanel: { marginTop: '20px', borderTop: '2px solid #E8E6E2', paddingTop: '20px' },
  panelTitle: { fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#1B2A4A', fontWeight: '700', marginBottom: '4px' },
  panelSub: { fontSize: '12px', color: '#3D3D3D', fontWeight: '300', lineHeight: '1.6', maxWidth: '560px', marginBottom: '4px' },
  siblingCode: { fontFamily: 'monospace', fontSize: '12px', fontWeight: '700', color: '#1B2A4A', background: '#F2F0EC', padding: '4px 10px', letterSpacing: '1px' },
  adminRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#F2F0EC', padding: '8px 12px' },
  adminEmail: { fontSize: '13px', color: '#1B2A4A', fontWeight: '500' },
  removeAdminBtn: { fontSize: '11px', color: '#C0392B', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  addAdminBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '10px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  msgBox: { fontSize: '12px', padding: '8px 12px' },
  msgError: { background: '#fdecea', color: '#a32d2d' },
  msgSuccess: { background: '#eaf3de', color: '#3b6d11' },
};
